import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClothingItems } from '../../hooks/useClothingItems';
import {
    useRemoveBgFromUploadMutation,
    useRemoveBgFromUrlMutation,
    useToggleClothingBackgroundMutation,
    useRemoveClothingBackgroundMutation,
    useUpdateClothingWithUploadMutation,
    useCheckExistingClothingMutation
} from '../../redux/apis/endpoints/closetApi';
import { processFileForUpload } from '../../utils/imageCompression';
import useVTODeletion from '../../hooks/useVTODeletion';
import { useCredits } from '../../hooks/useCredits';

const useClothingModalHandlers = ({ onClose, editItemId, isOpen }) => {
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    // State
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [clothesName, setClothesName] = useState('');
    const [isPublic, setIsPublic] = useState(true);
    const [originalIsPublic, setOriginalIsPublic] = useState(true);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [generalError, setGeneralError] = useState('');

    // Multi-file upload state
    const [multiFiles, setMultiFiles] = useState([]);
    const [multiUploadProgress, setMultiUploadProgress] = useState({ done: 0, total: 0, current: '' });
    const [isMultiUploading, setIsMultiUploading] = useState(false);

    // Delete state
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // VTO deletion warning state
    const [showDeletionWarning, setShowDeletionWarning] = useState(false);
    const [vtoCount, setVtoCount] = useState(0);
    const [deletionError, setDeletionError] = useState(null);

    // Name editing state
    const [editingName, setEditingName] = useState(false);
    const [tempName, setTempName] = useState('');
    const [nameError, setNameError] = useState(null);

    // Crop editor state
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editorImageUrl, setEditorImageUrl] = useState(null);
    const [isSavingCrop, setIsSavingCrop] = useState(false);

    // Background removal state
    const [showBgRemovalProcessing, setShowBgRemovalProcessing] = useState(false);
    const [bgRemovalError, setBgRemovalError] = useState(null);

    // Existing clothing check state
    const [existingClothing, setExistingClothing] = useState(null);
    const [isCheckingExisting, setIsCheckingExisting] = useState(false);

    // Track if item has removed background
    const [hasRemovedBg, setHasRemovedBg] = useState(false);
    const [useRemovedImage, setUseRemovedImage] = useState(false);

    // Track original values to detect changes
    const [originalClothesName, setOriginalClothesName] = useState('');

    // Track if initial data has been loaded
    const [initialDataLoaded, setInitialDataLoaded] = useState(false);

    // Determine if in edit mode
    const isEditMode = !!editItemId;

    // Credits hook
    const { balance, hasCredits, refetch: refetchCredits } = useCredits();

    // Use RTK Query hooks
    const {
        uploadClothing,
        updateClothing,
        deleteClothing,
        getItemById,
        isLoading,
        isUploading,
        isUpdating,
        isDeleting
    } = useClothingItems();

    // Background removal mutations
    const [removeBgFromUpload, { isLoading: isRemovingBgUpload }] = useRemoveBgFromUploadMutation();
    const [removeBgFromUrl, { isLoading: isRemovingBgUrl }] = useRemoveBgFromUrlMutation();
    const [toggleClothingBackground, { isLoading: isTogglingBg }] = useToggleClothingBackgroundMutation();
    const [removeClothingBackground, { isLoading: isRemovingClothingBg }] = useRemoveClothingBackgroundMutation();
    const [updateClothingWithUpload, { isLoading: isUpdatingWithUpload }] = useUpdateClothingWithUploadMutation();
    const [checkExistingClothing] = useCheckExistingClothingMutation();

    // VTO deletion hook
    const { checkVTOCount, deleteWithCascade, isDeleting: isDeletingWithCascade } = useVTODeletion();

    const isOperationInProgress = isUploading || isUpdating || isDeleting || isRemovingBgUpload ||
        isRemovingBgUrl || isDeletingWithCascade || isTogglingBg || isRemovingClothingBg ||
        isUpdatingWithUpload || isCheckingExisting;

    // Compute if any changes were made in edit mode
    const hasChanges = isEditMode ? (
        file !== null || // New image uploaded
        clothesName !== originalClothesName || // Name changed
        isPublic !== originalIsPublic // Share toggle changed
    ) : true; // Always enabled for new uploads

    // Reset state when modal opens or editItemId changes
    // This ensures previous item's data doesn't persist when switching items
    useEffect(() => {
        if (isOpen) {
            // Clear all state for fresh load
            setFile(null);
            setPreview(null);
            setClothesName('');
            setOriginalClothesName('');
            setIsPublic(true);
            setOriginalIsPublic(true);
            setUploadProgress(0);
            setGeneralError('');
            setDeleteError(null);
            setExistingClothing(null);
            setIsCheckingExisting(false);
            setHasRemovedBg(false);
            setUseRemovedImage(false);
            setInitialDataLoaded(false);
            setEditingName(false);
            setTempName('');
            setNameError(null);
            setBgRemovalError(null);

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    }, [isOpen, editItemId]);

    // Auto-process file dropped on clothing panel or selected from grid cards
    useEffect(() => {
        if (isOpen && !editItemId) {
            // Handle multiple files selected from upload card
            if (window.__pendingClothingFiles) {
                const pendingFiles = window.__pendingClothingFiles;
                window.__pendingClothingFiles = null;
                // Simulate the multi-file upload event
                const syntheticEvent = { target: { files: pendingFiles } };
                handleMultiFileUpload(syntheticEvent);
                return;
            }
            // Handle single file (from drag-drop or camera)
            if (window.__pendingClothingFile) {
                const pendingFile = window.__pendingClothingFile;
                window.__pendingClothingFile = null;
                // Simulate the file upload event
                const syntheticEvent = { target: { files: [pendingFile] } };
                handleLocalFileUpload(syntheticEvent);
            }
        }
    }, [isOpen, editItemId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Load item data if in edit mode
    useEffect(() => {
        if (initialDataLoaded || !isEditMode || !editItemId) return;

        const isLoggedIn = !!localStorage.getItem('token');

        if (!isLoggedIn) {
            // Get item from localStorage for guest mode
            const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');
            const item = guestClothes.find(item => item.id.toString() === editItemId);
            if (item) {
                setClothesName(item.clothesName || '');
                setOriginalClothesName(item.clothesName || '');
                setIsPublic(true);
                setPreview(item.image);
                setInitialDataLoaded(true);
            }
        } else {
            // Fetch from backend for logged-in users
            const item = getItemById(editItemId);
            if (item) {
                setClothesName(item.clothesName || '');
                setOriginalClothesName(item.clothesName || '');
                const itemIsPublic = item.isPublic ?? false;
                setIsPublic(itemIsPublic);
                setOriginalIsPublic(itemIsPublic);
                const imageUrl = typeof item.image === 'string'
                    ? item.image
                    : item.image?.url || item.image;

                const removedUrl = item.removedBgImage?.url;
                setHasRemovedBg(!!removedUrl);
                setUseRemovedImage(item.useRemovedImage === true);

                if (item.useRemovedImage && removedUrl) {
                    setPreview(removedUrl);
                } else {
                    setPreview(imageUrl);
                }
                setInitialDataLoaded(true);
            } else if (!isLoading) {
                setGeneralError('Item not found');
            }
        }
    }, [editItemId, isEditMode, getItemById, isLoading, initialDataLoaded]);

    // Handlers
    const handleFileInputClick = () => {
        fileInputRef.current?.click();
    };

    const handleLocalFileUpload = async (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            try {
                console.log('🔄 Clothing modal upload - processing file');

                const processedFile = await processFileForUpload(selectedFile, {
                    maxWidth: 2048,
                    maxHeight: 2048,
                    targetSizeMB: 5,
                    thresholdMB: 5,
                    minQuality: 0.5,
                    maxQuality: 0.9
                });

                // Set preview immediately for visual feedback
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreview(reader.result);
                };
                reader.readAsDataURL(processedFile);

                const isLoggedIn = !!localStorage.getItem('token');

                if (!isLoggedIn) {
                    // Guest mode: save to localStorage and close modal
                    const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');

                    // Read file as data URL for localStorage
                    const dataReader = new FileReader();
                    dataReader.onloadend = () => {
                        const newItem = {
                            id: Date.now(),
                            clothesName: '',
                            image: dataReader.result
                        };
                        guestClothes.push(newItem);
                        localStorage.setItem('guestClothes', JSON.stringify(guestClothes));
                        console.log('✅ Clothing saved to localStorage');

                        setTimeout(() => onClose(), 100);
                    };
                    dataReader.readAsDataURL(processedFile);
                    return;
                }

                // Logged-in user: check for existing and upload
                setFile(processedFile);
                setIsCheckingExisting(true);
                setExistingClothing(null);

                try {
                    const result = await checkExistingClothing(processedFile).unwrap();
                    if (result.exists && result.data) {
                        // Item already exists - show message but don't auto-close
                        setExistingClothing(result.data);
                        setClothesName(result.data.clothesName || '');
                        setIsCheckingExisting(false);
                        return; // Don't auto-upload if duplicate found
                    }
                } catch (err) {
                    console.error('Failed to check existing clothing:', err);
                } finally {
                    setIsCheckingExisting(false);
                }

                // No duplicate found - proceed with upload
                console.log('🔄 Auto-uploading clothing to backend...');
                setUploadProgress(20);

                try {
                    setUploadProgress(60);
                    await uploadClothing({
                        file: processedFile,
                        clothesName: '',
                        isPublic
                    });
                    setUploadProgress(100);

                    console.log('✅ Clothing uploaded successfully');
                    setTimeout(() => onClose(), 300);
                } catch (uploadErr) {
                    console.error('Upload failed:', uploadErr);
                    setGeneralError('Failed to upload clothing item. Please try again.');
                    setUploadProgress(0);
                }
            } catch (err) {
                console.error('File processing failed:', err);
                setGeneralError('Failed to process image. Please try another format.');
            }
        }
    };

    /**
     * Handle multi-file selection for batch upload
     */
    const handleMultiFileUpload = async (e) => {
        const selectedFiles = Array.from(e.target.files || []);
        if (selectedFiles.length === 0) return;

        const isLoggedIn = !!localStorage.getItem('token');
        if (!isLoggedIn) {
            setGeneralError('Batch upload requires login.');
            return;
        }

        // Filter to image files only
        const imageFiles = selectedFiles.filter(f =>
            f.type.startsWith('image/') || f.name.toLowerCase().endsWith('.heic')
        );

        if (imageFiles.length === 0) {
            setGeneralError('No valid image files selected.');
            return;
        }

        setIsMultiUploading(true);
        setMultiUploadProgress({ done: 0, total: imageFiles.length, current: '' });
        setGeneralError('');

        let successCount = 0;

        for (let i = 0; i < imageFiles.length; i++) {
            const file = imageFiles[i];
            const fileName = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
            setMultiUploadProgress({
                done: i,
                total: imageFiles.length,
                current: fileName || `Item ${i + 1}`
            });

            try {
                const processedFile = await processFileForUpload(file, {
                    maxWidth: 2048,
                    maxHeight: 2048,
                    targetSizeMB: 5,
                    thresholdMB: 5,
                    minQuality: 0.5,
                    maxQuality: 0.9
                });

                await uploadClothing({
                    file: processedFile,
                    clothesName: '',
                    isPublic
                });
                successCount++;
            } catch (err) {
                console.warn(`Failed to upload ${file.name}:`, err);
            }
        }

        setMultiUploadProgress({
            done: imageFiles.length,
            total: imageFiles.length,
            current: ''
        });
        setIsMultiUploading(false);

        if (successCount > 0) {
            setTimeout(() => onClose(), 500);
        } else {
            setGeneralError('All uploads failed. Please try again.');
        }
    };

    const handleRemoveExisting = async () => {
        if (!existingClothing?.id) return;

        try {
            setGeneralError('');
            await deleteClothing(existingClothing.id);
            setExistingClothing(null);
            setClothesName('');
        } catch (err) {
            setGeneralError('Failed to remove existing item. Please try again.');
            console.error('Remove existing error:', err);
        }
    };

    const handleStartEditName = () => {
        setTempName(clothesName);
        setEditingName(true);
    };

    const handleSaveName = () => {
        setClothesName(tempName);
        setEditingName(false);
    };

    const handleOpenEditor = () => {
        if (preview) {
            setEditorImageUrl(preview);
            setIsEditorOpen(true);
        }
    };

    const handleEditorSave = async (blob) => {
        setIsSavingCrop(true);
        try {
            const croppedFile = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
            setFile(croppedFile);

            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
                setIsEditorOpen(false);
                setIsSavingCrop(false);
            };
            reader.readAsDataURL(blob);
        } catch (err) {
            console.error('Crop save error:', err);
            setIsSavingCrop(false);
        }
    };

    const handleRemoveBackgroundClick = async () => {
        if (!preview) {
            setGeneralError('Please upload an image first.');
            return;
        }

        const isLoggedIn = !!localStorage.getItem('token');

        if (!isLoggedIn) {
            setGeneralError('Background removal requires login.');
            return;
        }

        const needsCredit = !(isEditMode && hasRemovedBg);
        if (needsCredit && !hasCredits) {
            setGeneralError('No credits remaining. Buy credits or invite friends to earn more.');
            return;
        }

        try {
            setGeneralError('');
            setBgRemovalError(null);
            setShowBgRemovalProcessing(true);

            if (isEditMode && editItemId) {
                const result = await removeClothingBackground(editItemId).unwrap();
                if (result.removedBgImage?.url) {
                    setHasRemovedBg(true);
                    setUseRemovedImage(true);
                    setPreview(result.removedBgImage.url);
                }
                refetchCredits?.();
                setShowBgRemovalProcessing(false);
                return;
            }

            let result;
            if (file) {
                result = await removeBgFromUpload(file).unwrap();
            } else {
                result = await removeBgFromUrl(preview).unwrap();
            }

            if (result && result.url) {
                setPreview(result.url);
                setFile(null);
            } else {
                setBgRemovalError('Failed to remove background. Please try again.');
            }
            setShowBgRemovalProcessing(false);
        } catch (err) {
            setShowBgRemovalProcessing(false);
            setBgRemovalError(err?.data?.message || err?.message || 'Failed to remove background. Please try again.');
            console.error('Remove background error:', err);
        }
    };

    const handleToggleBackground = async () => {
        if (!isEditMode || !editItemId || !hasRemovedBg) return;

        try {
            const result = await toggleClothingBackground(editItemId).unwrap();
            const newUseRemoved = result.useRemovedImage;
            setUseRemovedImage(newUseRemoved);

            if (newUseRemoved && result.removedBgImage?.url) {
                setPreview(result.removedBgImage.url);
            } else if (result.image?.url) {
                setPreview(result.image.url);
            }
        } catch (err) {
            setBgRemovalError(err?.data?.message || err?.message || 'Failed to toggle background.');
            console.error('Toggle background error:', err);
        }
    };

    const handleDeleteClick = async () => {
        const isLoggedIn = !!localStorage.getItem('token');

        if (!isLoggedIn) {
            setDeleteConfirmOpen(true);
        } else {
            try {
                const count = await checkVTOCount(editItemId, 'clothing-item');
                if (count > 0) {
                    setVtoCount(count);
                    setShowDeletionWarning(true);
                } else {
                    setDeleteConfirmOpen(true);
                }
            } catch (error) {
                console.error('Error checking VTO count:', error);
                setDeleteConfirmOpen(true);
            }
        }
    };

    const handleConfirmDelete = async () => {
        const isLoggedIn = !!localStorage.getItem('token');

        if (!isLoggedIn) {
            const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');
            const updatedClothes = guestClothes.filter(item => item.id.toString() !== editItemId);
            localStorage.setItem('guestClothes', JSON.stringify(updatedClothes));
            setDeleteConfirmOpen(false);
            onClose();
            navigate('/my-closet');
        } else {
            try {
                await deleteClothing(editItemId);
                setDeleteConfirmOpen(false);
                onClose();
                navigate('/my-closet');
            } catch (err) {
                setDeleteError('Failed to delete item. Please try again.');
                console.error('Delete error:', err);
            }
        }
    };

    const handleCancelDelete = () => {
        setDeleteConfirmOpen(false);
        setDeleteError(null);
    };

    const handleCancelDeletionWarning = () => {
        setShowDeletionWarning(false);
        setVtoCount(0);
        setDeletionError(null);
    };

    const handleConfirmCascadeDelete = async () => {
        try {
            setDeletionError(null);
            await deleteWithCascade(editItemId, 'clothing-item');
            setShowDeletionWarning(false);
            onClose();
            navigate('/my-closet');
        } catch (error) {
            console.error('❌ Cascade delete failed:', error);
            setDeletionError(error.message || 'Failed to delete clothing item and VTO results');
        }
    };

    const handleGuestSubmit = async () => {
        if (!file && !isEditMode) {
            setGeneralError('Please provide an image for your clothing item');
            return;
        }

        const guestClothes = JSON.parse(localStorage.getItem('guestClothes') || '[]');

        if (isEditMode) {
            const updatedClothes = guestClothes.map(item => {
                if (item.id.toString() === editItemId) {
                    return {
                        ...item,
                        clothesName,
                        image: preview
                    };
                }
                return item;
            });
            localStorage.setItem('guestClothes', JSON.stringify(updatedClothes));
        } else {
            const newItem = {
                id: Date.now(),
                clothesName,
                image: preview
            };
            guestClothes.push(newItem);
            localStorage.setItem('guestClothes', JSON.stringify(guestClothes));
        }

        onClose();
        navigate('/my-closet');
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setGeneralError('');
        setUploadProgress(0);

        if (!file && !preview && !isEditMode) {
            setGeneralError('Please provide an image for your clothing item');
            return;
        }

        const isLoggedIn = !!localStorage.getItem('token');

        if (!isLoggedIn) {
            await handleGuestSubmit();
            return;
        }

        try {
            setUploadProgress(20);

            if (isEditMode) {
                setUploadProgress(60);

                if (file) {
                    await updateClothingWithUpload({
                        id: editItemId,
                        file,
                        clothesName,
                        isPublic
                    }).unwrap();
                } else {
                    await updateClothing({
                        id: editItemId,
                        clothesName,
                        isPublic
                    });
                }
                setUploadProgress(100);
            } else {
                if (!file) {
                    setGeneralError('Please select an image file');
                    return;
                }

                setUploadProgress(60);
                await uploadClothing({
                    file,
                    clothesName,
                    isPublic
                });
                setUploadProgress(100);
            }

            setTimeout(() => {
                onClose();
                navigate('/my-closet');
            }, 500);

        } catch (err) {
            setGeneralError(`Failed to ${isEditMode ? 'update' : 'upload'} clothing item. Please try again.`);
            console.error('Error:', err);
            setUploadProgress(0);
        }
    };

    return {
        // Refs
        fileInputRef,

        // State
        deleteConfirmOpen,
        deleteError,
        bgRemovalError,
        setBgRemovalError,
        editingName,
        tempName,
        setTempName,
        nameError,
        setNameError,
        isEditorOpen,
        setIsEditorOpen,
        editorImageUrl,
        isSavingCrop,
        showDeletionWarning,
        vtoCount,
        deletionError,
        showBgRemovalProcessing,
        existingClothing,
        isCheckingExisting,
        uploadProgress,
        generalError,
        setGeneralError,

        // Computed
        isOperationInProgress,
        isEditMode,
        preview,
        clothesName,
        isPublic,
        setIsPublic,
        hasChanges,

        // Data
        creditBalance: balance,
        hasCredits,
        daysUntilReset: 30, // TODO: Calculate actual days
        hasRemovedBg,
        useRemovedImage,

        // Handlers
        handleFileInputClick,
        handleLocalFileUpload,
        handleRemoveExisting,
        handleRemoveBackgroundClick,
        handleToggleBackground,
        handleConfirmDelete,
        handleCancelDelete,
        handleCancelDeletionWarning,
        handleConfirmCascadeDelete,
        handleSaveName,
        handleStartEditName,
        handleOpenEditor,
        handleEditorSave,
        handleDeleteClick,
        handleSubmit,

        // Multi-upload
        handleMultiFileUpload,
        multiFiles,
        multiUploadProgress,
        isMultiUploading
    };
};

export default useClothingModalHandlers;
